import { PubSub } from 'Amplify';
import { store } from '@lifepowr/components/src/store';
import { setSystem, setSystemConnected, setSystemRtData, setSystemShadow } from '@lifepowr/components/src/store/system';
import { nanoid } from 'nanoid';
import { Hub } from 'aws-amplify';

export class DeviceStateStoreService {
  public updateConnectionState(connected: boolean) {
    store.dispatch(setSystemConnected(connected));
  }

  public updateRealTimeData(realTimeData: any) {
    store.dispatch(setSystemRtData(realTimeData));
  }

  public updateThingData(thing: any) {
    const { connectivity: { connected } = {} } = thing;

    store.dispatch(setSystem(thing));
    store.dispatch(setSystemConnected(connected));
  }

  public updateThingShadow(shadowData: any, name: string) {
    store.dispatch(setSystemShadow(shadowData, name));
  }
}

export type DynamicDataStatusListener = (isConnected: boolean) => void;
export type DynamicDataStatusUnsubscribeListener = () => void;

type HubCapsule = {
  channel: string;
  payload: {
    event: string;
    data?: any;
    message?: string;
  };
  source: string;
};

/**
 * DeviceDynamicDataSubscriber is responsible for subscribing/unsubscribing to/from AWS topics
 * and updating Redux store with the dynamic IoT shadow updates from the devices.
 *
 * @note It supports subscriptions to only ONE device at a time.
 */
export class DeviceDynamicDataSubscriber {
  private thingName: string = '';
  private subscribedShadowNames: string[] = ['', 'batteries', 'batControlMode', 'settings', 'devices', 'diagnostics'];
  private mqttSubscriptions: Record<string, any> = {};
  private deviceStateStoreService: DeviceStateStoreService;
  public static readonly clientToken = nanoid();

  protected listeners = new Set<DynamicDataStatusListener>();

  constructor(deviceStateStoreService: DeviceStateStoreService) {
    this.deviceStateStoreService = deviceStateStoreService;

    Hub.listen('pubsub', this.pubSubCallback.bind(this));
  }

  private pubSubCallback(capsule: HubCapsule): void {
    //const connected = capsule.payload.data?.connectionState.toLowerCase() == 'connected';
    const disconnected = capsule.payload.data?.connectionState.toLowerCase() === 'disconnected';

    if (disconnected) {
      this.notifyListeners(false);
    }
  }

  /**
   * Installs a new listener and returns the unsubscribe function.
   */
  public subscribe(listener: DynamicDataStatusListener): DynamicDataStatusUnsubscribeListener {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  protected notifyListeners(isConnected: boolean) {
    this.listeners.forEach((listener) => listener(isConnected));
  }

  private createShadowPrefix = (deviceName: string, shadowName: string): string => {
    const response = `$aws/things/${deviceName}/shadow`;
    if (shadowName) {
      return response + `/name/${shadowName}`;
    }
    return response;
  };

  /**
   * Subscribes to the AWS IoT shadow ${name}/update/accepted topic for the currently selected device.
   * It registers a function callback to update the Redux store with the shadow data when changed.
   */
  private hookShadowOnSocket = (name: string) => {
    const shadowPrefix = this.createShadowPrefix(this.thingName, name);

    this.subscribeToMqtt(`${shadowPrefix}/update/accepted`, ({ value: { state, metadata } }: any) => {
      const { desired, reported } = state || {};

      this.deviceStateStoreService.updateThingShadow(
        {
          desired,
          reported,
          metadata,
        },
        name,
      );
    });
  };

  /**
   * Unsubscribes from the AWS IoT shadow ${name}/update/accepted topic of the current device.
   */
  private unHookShadowOnSocket = (shadowName: string) => {
    if (!this.thingName) {
      return;
    }

    const shadowPrefix = this.createShadowPrefix(this.thingName, shadowName);
    this.unsubscribeFromMqtt(`${shadowPrefix}/update/accepted`);
  };

  private async cleanupDeviceRegistrationsAsync() {
    if (this.thingName !== '') {
      await this.unsubscribeFromAwsAsync();
    }
  }

  private subscribeToMqtt(topic: string, cb: Function) {
    this.mqttSubscriptions[topic] = PubSub.subscribe(topic).subscribe({
      next: (data) => cb(data),
      error: (error) => console.error(error),
      complete: () => console.log(`subscribe topic[${topic}] Done`),
    });
  }

  private unsubscribeFromMqtt(topic: string) {
    const { [topic]: selectedSubscription, ...restOfSubscriptions } = this.mqttSubscriptions;

    if (selectedSubscription) {
      selectedSubscription.unsubscribe();

      this.mqttSubscriptions = restOfSubscriptions;
    }
  }

  /**
   * Starts subscribing to AWS topics for the given ${thingName}.
   * If there is a previous subscription, it will be unsubscribed first.
   */
  public async subscribeToAwsAsync(thingName: string) {
    if (this.thingName === thingName) {
      return;
    }

    await this.cleanupDeviceRegistrationsAsync();

    this.thingName = thingName;
    this.subscribedShadowNames.forEach(this.hookShadowOnSocket);

    this.subscribeToMqtt(`fleet/devices/${thingName}/data`, ({ value }: any) => {
      this.deviceStateStoreService.updateRealTimeData(value || {});
    });

    this.subscribeToMqtt(`$aws/events/thing/${thingName}/updated`, ({ value }: any) => {
      const { attributes } = value;
      if (attributes) {
        const newSystem = Object.assign({}, store.getState().system, { attributes });
        this.deviceStateStoreService.updateThingData(newSystem);
      }
    });

    this.subscribeToMqtt(`$aws/events/presence/connected/${thingName}`, () => {
      this.deviceStateStoreService.updateConnectionState(true);
    });

    this.subscribeToMqtt(`$aws/events/presence/disconnected/${thingName}`, () => {
      this.deviceStateStoreService.updateConnectionState(false);
    });

    this.notifyListeners(true);
  }

  public async unsubscribeFromAwsAsync() {
    if (!this.thingName) {
      return;
    }

    this.unsubscribeFromMqtt(`fleet/devices/${this.thingName}/data`);
    this.unsubscribeFromMqtt(`$aws/events/thing/${this.thingName}/updated`);
    this.unsubscribeFromMqtt(`$aws/events/presence/connected/${this.thingName}`);
    this.unsubscribeFromMqtt(`$aws/events/presence/disconnected/${this.thingName}`);

    this.subscribedShadowNames.forEach(this.unHookShadowOnSocket);

    this.thingName = '';

    this.notifyListeners(false);
  }
}

export const deviceStateStoreService = new DeviceStateStoreService();
export const deviceDynamicDataSubscriber = new DeviceDynamicDataSubscriber(deviceStateStoreService);
